import { QualifierWithSelection } from "./refineFeaturePanel";
import { useMemo } from "react";
import { Qualifier } from "fe-common/graphql";
import { isQualifierEqual } from "../../../util/QualifierComparator";
import { ChildrenSetType } from "@kahunmedical/common-wtypes";
import { Values } from "shared";

const ancestors = (q: QualifierWithSelection) =>
  q.qualifier.value.parents.features;

const isDisjoint = (qFeatures) =>
  qFeatures.childrenSetType === ChildrenSetType.DISJOINT ||
  qFeatures.childrenSetType === ChildrenSetType.COMPLETE;

const isAncestor = (
  q1: QualifierWithSelection,
  q2: QualifierWithSelection,
): boolean =>
  ancestors(q1).some((q1Ancestor) => q1Ancestor.id === q2.qualifier.value.id);

const shareDisjointAncestor = (
  q1: QualifierWithSelection,
  q2: QualifierWithSelection,
): boolean =>
  ancestors(q1)
    .filter(isDisjoint)
    .some((q1Ancestor) =>
      ancestors(q2).some((q2Ancestor) => q2Ancestor.id === q1Ancestor.id),
    );

export function useSetDisabledQualifiers(qualifiers: QualifierWithSelection[]) {
  const relationToQualifiers = useGroupQualifiersByGroup(qualifiers);
  useMemo(() => {
    Object.values(relationToQualifiers).forEach((qualifiers) => {
      const selectedQualifiers = qualifiers.filter((q) => q.selected);

      qualifiers.forEach((q) => {
        q.disabled =
          !q.selected &&
          selectedQualifiers.some(
            (selectedQ) =>
              !isAncestor(selectedQ, q) &&
              !isAncestor(q, selectedQ) &&
              shareDisjointAncestor(selectedQ, q),
          );

        q.canBeNegated = q.qualifier.value.parents.features.every(
          (qf) => !isDisjoint(qf),
        );
      });
    });
  }, [relationToQualifiers]);
}

export function useSetSelectedQualifiers(
  QualifierWithSelection: QualifierWithSelection[],
  selectedQualifier: Qualifier[],
): QualifierWithSelection[] {
  return useMemo(
    () =>
      QualifierWithSelection.map((q) => ({
        qualifier: q.qualifier,
        selected: selectedQualifier?.some((q1) =>
          isQualifierEqual(q.qualifier, q1),
        ),
        negated: selectedQualifier?.some((q1) =>
          isQualifierEqual(q.qualifier, q1),
        )
          ? selectedQualifier?.find((q1) => isQualifierEqual(q.qualifier, q1))
              .negate
          : false,
      })),
    [QualifierWithSelection, selectedQualifier],
  );
}

const relationName = (q) => q.relation.name;
const qualifierId = (q) => q.qualifier?.value?.id;
const excludedQualifiers = [Values.ACUTE, Values.CHRONIC];
const isExcludedQualifier = (q) => excludedQualifiers.includes(qualifierId(q));

export function useGroupQualifiersByGroup(
  qualifiers: QualifierWithSelection[],
) {
  const relationToQualifiers: {
    [relation: string]: QualifierWithSelection[];
  } = useMemo(
    () =>
      qualifiers.reduce((acc, q) => {
        const rName = relationName(q.qualifier);
        acc[rName] = acc[rName] || [];
        !isExcludedQualifier(q) && acc[rName].push(q);
        return acc;
      }, {}),
    [qualifiers],
  );

  return relationToQualifiers;
}

export function useOrderRelations(qualifiers: QualifierWithSelection[]) {
  //since the qualifiers are ordered, the relations will be ordered by their most used qualifier.
  //This is dedup while keeping the order
  const orderedRelations: string[] = useMemo(
    () =>
      qualifiers
        .map((q) => q.qualifier)
        .map(relationName)
        .filter(
          (relationName, index, relationsNames) =>
            relationsNames.indexOf(relationName) === index,
        ),
    [qualifiers],
  );

  return orderedRelations;
}
